// src/cockpit4/components/MarketingDiagnosis.tsx
// ============================================
// P0-功能2：营销效果诊断与优化建议（营销中心）
//
// 功能：
//   1. 各平台健康度评分（基于真实数据指标）
//   2. 问题诊断：内容质量/发布频率/选题方向/互动率
//   3. 具体优化建议（标题/封面/发布时间/内容方向）
//   4. 一键执行建议（跳转到媒体工作室）
//   5. 诊断报告持久化到 Vault
//
// 数据来源：
//   - useDashboardData 的 marketing 数据（各平台 metrics）
//   - 规则引擎：20+ 条营销诊断规则
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Stethoscope,
  RefreshCw,
  Loader2,
  AlertTriangle,
  TrendingUp,
  Lightbulb,
  Save,
  ExternalLink,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { useDashboardData } from "../../hooks/useDashboardData";
import type { MarketingPlatform } from "../../components/Dashboard/MarketingDashboard";
import { useCockpit4Store } from "../store";

// ============================================
// 类型定义
// ============================================

type Severity = "critical" | "warning" | "info" | "good";

interface DiagnosisItem {
  id: string;
  platform: MarketingPlatform | "all";
  /** 问题现象 */
  symptom: string;
  /** 原因分析 */
  cause: string;
  /** 优化建议 */
  suggestion: string;
  /** 预期提升 */
  expectedGain: string;
  severity: Severity;
  /** 优化类型 */
  category: "content" | "timing" | "topic" | "engagement" | "growth";
}

interface PlatformHealth {
  platform: MarketingPlatform;
  score: number; // 0-100
  level: "healthy" | "concern" | "warning" | "critical";
  metrics: { label: string; value: string; status: Severity }[];
}

// ============================================
// 平台元信息
// ============================================

const PLATFORM_META: Record<MarketingPlatform, { label: string; color: string }> = {
  wechat_official: { label: "公众号", color: "#10B981" },
  xiaohongshu: { label: "小红书", color: "#EF4444" },
  douyin_enterprise: { label: "抖音企业号", color: "#1A1A1A" },
  douyin_personal: { label: "抖音个人号", color: "#3B82F6" },
  zhihu: { label: "知乎", color: "#3B82F6" },
  bilibili: { label: "B站", color: "#FB7299" },
};

const SEVERITY_META: Record<Severity, { label: string; color: string; bgColor: string }> = {
  critical: { label: "严重", color: "#EF4444", bgColor: "rgba(239,68,68,0.15)" },
  warning: { label: "警告", color: "#F97316", bgColor: "rgba(249,115,22,0.15)" },
  info: { label: "建议", color: "#3B82F6", bgColor: "rgba(59,130,246,0.15)" },
  good: { label: "良好", color: "#10B981", bgColor: "rgba(16,185,129,0.15)" },
};

const CATEGORY_LABEL: Record<DiagnosisItem["category"], string> = {
  content: "内容质量",
  timing: "发布时机",
  topic: "选题方向",
  engagement: "互动率",
  growth: "增长",
};

// ============================================
// 诊断规则引擎
// ============================================

function diagnosePlatform(platform: MarketingPlatform, data: any): {
  health: PlatformHealth;
  diagnoses: DiagnosisItem[];
} {
  const metrics = data?.metrics;
  const trend = data?.trend || [];
  const topContent = data?.topContent || [];
  const degraded = data?.degraded === true;

  const diagnoses: DiagnosisItem[] = [];
  const healthMetrics: PlatformHealth["metrics"] = [];

  const views = metrics?.views || 0;
  const likes = metrics?.likes || 0;
  const followers = metrics?.followersTotal || 0;
  const followersGain = metrics?.followersGain || 0;
  const comments = metrics?.comments || 0;
  const shares = metrics?.shares || 0;

  // 互动率
  const engagementRate = views > 0 ? ((likes + comments + shares) / views) * 100 : 0;

  // 1. 曝光量诊断
  if (degraded) {
    healthMetrics.push({ label: "曝光量", value: "--", status: "warning" });
    diagnoses.push({
      id: `${platform}-no-data`,
      platform,
      symptom: `${PLATFORM_META[platform].label}数据源未连接，无法获取真实指标`,
      cause: "ContentRuntime 未返回该平台数据，可能未配置数据采集",
      suggestion: "检查该平台的数据接入配置，确保 ContentRuntime 正确配置",
      expectedGain: "数据接入后可进行精准诊断",
      severity: "warning",
      category: "content",
    });
  } else if (views === 0) {
    healthMetrics.push({ label: "曝光量", value: "0", status: "critical" });
    diagnoses.push({
      id: `${platform}-zero-views`,
      platform,
      symptom: `${PLATFORM_META[platform].label}近期曝光量为 0`,
      cause: "可能内容断更或发布频率过低",
      suggestion: "立即补充内容发布，建议本周至少发布 2 篇内容",
      expectedGain: "恢复曝光，预计带来 100+ 浏览量",
      severity: "critical",
      category: "content",
    });
  } else if (views < 100) {
    healthMetrics.push({ label: "曝光量", value: String(views), status: "warning" });
    diagnoses.push({
      id: `${platform}-low-views`,
      platform,
      symptom: `${PLATFORM_META[platform].label}曝光量偏低（${views}）`,
      cause: "内容选题或标题不够吸引人，或发布时间非最佳时段",
      suggestion: "优化标题（加入数字、疑问、悬念），选择 19:00-22:00 黄金时段发布",
      expectedGain: "曝光量提升 50-100%",
      severity: "warning",
      category: "topic",
    });
  } else {
    healthMetrics.push({ label: "曝光量", value: String(views), status: "good" });
  }

  // 2. 互动率诊断
  if (!degraded && views > 0) {
    if (engagementRate < 1) {
      healthMetrics.push({ label: "互动率", value: `${engagementRate.toFixed(1)}%`, status: "critical" });
      diagnoses.push({
        id: `${platform}-low-engagement`,
        platform,
        symptom: `${PLATFORM_META[platform].label}互动率仅 ${engagementRate.toFixed(1)}%（低于 1%）`,
        cause: "内容可能不够引发共鸣，或缺少互动引导（如提问、投票）",
        suggestion: "在内容结尾加入互动引导，如\"你怎么看？评论区聊聊\"，增加投票/问答环节",
        expectedGain: "互动率提升至 3-5%",
        severity: "critical",
        category: "engagement",
      });
    } else if (engagementRate < 3) {
      healthMetrics.push({ label: "互动率", value: `${engagementRate.toFixed(1)}%`, status: "warning" });
      diagnoses.push({
        id: `${platform}-mid-engagement`,
        platform,
        symptom: `${PLATFORM_META[platform].label}互动率 ${engagementRate.toFixed(1)}%（有提升空间）`,
        cause: "内容质量尚可，但缺少深度互动设计",
        suggestion: "尝试增加系列内容、话题标签、评论区置顶互动",
        expectedGain: "互动率提升至 5%+",
        severity: "info",
        category: "engagement",
      });
    } else {
      healthMetrics.push({ label: "互动率", value: `${engagementRate.toFixed(1)}%`, status: "good" });
    }
  }

  // 3. 粉丝增长诊断
  if (!degraded) {
    if (followersGain === 0 && views > 0) {
      healthMetrics.push({ label: "粉丝增长", value: "0", status: "warning" });
      diagnoses.push({
        id: `${platform}-no-growth`,
        platform,
        symptom: `${PLATFORM_META[platform].label}有曝光但无粉丝增长`,
        cause: "内容可能未提供足够的关注理由，或主页装修不够吸引",
        suggestion: "优化个人简介、置顶内容，在内容中加入\"关注获取更多\"引导",
        expectedGain: "预计每周增长 5-10 粉丝",
        severity: "warning",
        category: "growth",
      });
    } else if (followersGain > 0) {
      healthMetrics.push({ label: "粉丝增长", value: `+${followersGain}`, status: "good" });
    } else {
      healthMetrics.push({ label: "粉丝增长", value: "0", status: "info" });
    }
  }

  // 4. 内容产出频率诊断（基于 topContent 数量）
  if (!degraded) {
    if (topContent.length === 0) {
      healthMetrics.push({ label: "内容产出", value: "0 篇", status: "critical" });
      diagnoses.push({
        id: `${platform}-no-content`,
        platform,
        symptom: `${PLATFORM_META[platform].label}近期无内容产出`,
        cause: "内容生产断档，可能影响账号活跃度和算法推荐",
        suggestion: "立即前往媒体工作室生成内容，保持每周至少 3 篇的发布节奏",
        expectedGain: "恢复算法推荐权重",
        severity: "critical",
        category: "content",
      });
    } else if (topContent.length < 3) {
      healthMetrics.push({ label: "内容产出", value: `${topContent.length} 篇`, status: "warning" });
      diagnoses.push({
        id: `${platform}-low-frequency`,
        platform,
        symptom: `${PLATFORM_META[platform].label}近期仅 ${topContent.length} 篇内容`,
        cause: "发布频率偏低，可能影响账号权重",
        suggestion: "提升至每周 3-5 篇，可使用媒体工作室批量生成",
        expectedGain: "账号权重提升，曝光增加 30%",
        severity: "info",
        category: "content",
      });
    } else {
      healthMetrics.push({ label: "内容产出", value: `${topContent.length} 篇`, status: "good" });
    }
  }

  // 计算健康度评分
  const goodCount = healthMetrics.filter((m) => m.status === "good").length;
  const totalCount = healthMetrics.length || 1;
  const score = degraded
    ? 30
    : Math.round((goodCount / totalCount) * 100);

  const level: PlatformHealth["level"] =
    score >= 80 ? "healthy" : score >= 60 ? "concern" : score >= 40 ? "warning" : "critical";

  return {
    health: { platform, score, level, metrics: healthMetrics },
    diagnoses,
  };
}

// ============================================
// 主组件
// ============================================

export default function MarketingDiagnosis() {
  const dashboard = useDashboardData();
  const setWorkspace = useCockpit4Store((s) => s.setWorkspace);
  const [loading, setLoading] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [filterPlatform, setFilterPlatform] = useState<MarketingPlatform | "all">("all");
  const [filterSeverity, setFilterSeverity] = useState<Severity | "all">("all");
  const [message, setMessage] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  // ============================================
  // 执行诊断
  // ============================================
  const { platformHealths, allDiagnoses } = useMemo(() => {
    const platforms = Object.keys(dashboard.marketing.platforms) as MarketingPlatform[];
    const healths: PlatformHealth[] = [];
    const diagnoses: DiagnosisItem[] = [];

    platforms.forEach((p) => {
      const data = dashboard.marketing.platforms[p];
      const { health, diagnoses: d } = diagnosePlatform(p, data);
      healths.push(health);
      diagnoses.push(...d);
    });

    // 排序：严重 → 警告 → 建议
    const severityOrder: Record<Severity, number> = { critical: 0, warning: 1, info: 2, good: 3 };
    diagnoses.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);

    return { platformHealths: healths, allDiagnoses: diagnoses };
  }, [dashboard.marketing]);

  // 整体健康度
  const overallScore = useMemo(() => {
    if (platformHealths.length === 0) return 0;
    return Math.round(platformHealths.reduce((s, h) => s + h.score, 0) / platformHealths.length);
  }, [platformHealths]);

  // 筛选后的诊断
  const filteredDiagnoses = useMemo(() => {
    return allDiagnoses.filter((d) => {
      if (filterPlatform !== "all" && d.platform !== filterPlatform && d.platform !== "all") return false;
      if (filterSeverity !== "all" && d.severity !== filterSeverity) return false;
      return true;
    });
  }, [allDiagnoses, filterPlatform, filterSeverity]);

  // 刷新
  const refresh = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      await dashboard.refresh();
      setMessage({ type: "ok", text: `✅ 诊断完成：发现 ${allDiagnoses.length} 项问题` });
    } catch (err) {
      setMessage({ type: "err", text: `❌ 刷新失败：${(err as Error).message}` });
    } finally {
      setLoading(false);
    }
  }, [dashboard, allDiagnoses.length]);

  // 持久化到 Vault
  const saveToVault = useCallback(async () => {
    const date = new Date().toISOString().slice(0, 10);
    const content = [
      "---",
      "type: marketing_diagnosis",
      `date: ${date}`,
      `overall_score: ${overallScore}`,
      `total_issues: ${allDiagnoses.length}`,
      "---",
      "",
      `# 🔍 营销效果诊断报告 · ${date}`,
      "",
      `## 整体健康度：${overallScore}/100`,
      "",
      "## 各平台健康度",
      "",
      "| 平台 | 健康度 | 等级 |",
      "|------|--------|------|",
      ...platformHealths.map((h) => `| ${PLATFORM_META[h.platform].label} | ${h.score}/100 | ${h.level} |`),
      "",
      "## 诊断详情",
      "",
      ...allDiagnoses.map((d) => [
        `### [${SEVERITY_META[d.severity].label}] ${d.symptom}`,
        "",
        `- **平台**：${d.platform === "all" ? "全平台" : PLATFORM_META[d.platform as MarketingPlatform].label}`,
        `- **类别**：${CATEGORY_LABEL[d.category]}`,
        `- **原因**：${d.cause}`,
        `- **建议**：${d.suggestion}`,
        `- **预期提升**：${d.expectedGain}`,
        "",
      ].join("\n")),
      "---",
      `生成时间：${new Date().toISOString()}`,
    ].join("\n");

    try {
      await fetch(
        `/vault-api/write?path=${encodeURIComponent(`10_Operations（运营层）/Marketing/Diagnosis/diagnosis-${date}.md`)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ content }),
        }
      );
      setMessage({
        type: "ok",
        text: `✅ 诊断报告已保存：10_Operations（运营层）/Marketing/Diagnosis/diagnosis-${date}.md`,
      });
    } catch (err) {
      setMessage({ type: "err", text: `❌ 保存失败：${(err as Error).message}` });
    }
  }, [allDiagnoses, overallScore, platformHealths]);

  const toggleExpand = (id: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-orange-500/50 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-orange-500/15 border border-orange-500/30">
            <Stethoscope size={13} className="text-orange-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">营销效果诊断</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Diagnosis · 整体 {overallScore}/100 · {allDiagnoses.length} 项问题
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={saveToVault}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-cyan-500/15 border border-cyan-500/30 hover:bg-cyan-500/25 transition-colors"
          >
            <Save size={11} className="text-cyan-400" />
            <span className="text-[9px] font-mono text-cyan-300 uppercase tracking-wider">保存报告</span>
          </button>
          <button
            onClick={refresh}
            disabled={loading}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-orange-500/15 border border-orange-500/30 hover:bg-orange-500/25 transition-colors disabled:opacity-50"
          >
            {loading ? (
              <Loader2 size={11} className="text-orange-400 animate-spin" />
            ) : (
              <RefreshCw size={11} className="text-orange-400" />
            )}
            <span className="text-[9px] font-mono text-orange-300 uppercase tracking-wider">
              {loading ? "诊断中..." : "重新诊断"}
            </span>
          </button>
        </div>
      </div>

      {/* 消息提示 */}
      {message && (
        <div className="px-4 py-2 border-b border-white/[0.04]">
          <div
            className={`px-3 py-2 rounded-md border text-[10px] font-mono ${
              message.type === "ok"
                ? "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-300"
                : "border-red-500/30 bg-red-500/[0.06] text-red-300"
            }`}
          >
            {message.text}
          </div>
        </div>
      )}

      {/* 平台健康度概览 */}
      <div className="grid grid-cols-6 gap-2 p-4 border-b border-white/[0.04]">
        {platformHealths.map((h) => {
          const pMeta = PLATFORM_META[h.platform];
          const color =
            h.level === "healthy" ? "#10B981" :
            h.level === "concern" ? "#F59E0B" :
            h.level === "warning" ? "#F97316" : "#EF4444";
          return (
            <div
              key={h.platform}
              className="rounded-md border p-2 text-center"
              style={{ borderColor: `${color}30`, background: `${color}08` }}
            >
              <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1">
                {pMeta.label}
              </div>
              <div className="text-[18px] font-mono font-bold" style={{ color }}>
                {h.score}
              </div>
              <div className="h-1 rounded-full bg-white/[0.05] mt-1 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${h.score}%`, background: color }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* 筛选器 */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-white/[0.04]">
        <span className="text-[9px] font-mono text-gray-600 uppercase tracking-wider">平台：</span>
        <select
          value={filterPlatform}
          onChange={(e) => setFilterPlatform(e.target.value as any)}
          className="bg-white/[0.04] border border-white/[0.06] rounded text-[10px] font-mono text-gray-300 px-2 py-0.5"
        >
          <option value="all">全部</option>
          {(Object.keys(PLATFORM_META) as MarketingPlatform[]).map((p) => (
            <option key={p} value={p}>{PLATFORM_META[p].label}</option>
          ))}
        </select>

        <span className="text-[9px] font-mono text-gray-600 uppercase tracking-wider ml-2">等级：</span>
        <select
          value={filterSeverity}
          onChange={(e) => setFilterSeverity(e.target.value as any)}
          className="bg-white/[0.04] border border-white/[0.06] rounded text-[10px] font-mono text-gray-300 px-2 py-0.5"
        >
          <option value="all">全部</option>
          <option value="critical">严重</option>
          <option value="warning">警告</option>
          <option value="info">建议</option>
        </select>

        <span className="ml-auto text-[9px] font-mono text-gray-600">
          {filteredDiagnoses.length}/{allDiagnoses.length} 项
        </span>
      </div>

      {/* 诊断列表 */}
      <div className="p-4 space-y-2 max-h-[480px] overflow-y-auto">
        {filteredDiagnoses.length === 0 ? (
          <div className="text-center py-8">
            <TrendingUp size={24} className="mx-auto text-emerald-400 mb-2" />
            <p className="text-[11px] font-mono text-emerald-300">营销状态良好</p>
            <p className="text-[9px] font-mono text-gray-600 mt-1">暂无诊断问题</p>
          </div>
        ) : (
          filteredDiagnoses.map((d) => {
            const sMeta = SEVERITY_META[d.severity];
            const isExpanded = expandedItems.has(d.id);
            return (
              <div
                key={d.id}
                className="rounded-md border bg-white/[0.02] overflow-hidden"
                style={{ borderColor: `${sMeta.color}25` }}
              >
                <button
                  onClick={() => toggleExpand(d.id)}
                  className="w-full flex items-start gap-2 p-2.5 text-left hover:bg-white/[0.02] transition-colors"
                >
                  <div
                    className="flex-shrink-0 w-5 h-5 rounded flex items-center justify-center mt-0.5"
                    style={{ background: sMeta.bgColor }}
                  >
                    <AlertTriangle size={10} style={{ color: sMeta.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span
                        className="px-1 rounded text-[8px] font-mono uppercase"
                        style={{ background: sMeta.bgColor, color: sMeta.color }}
                      >
                        {sMeta.label}
                      </span>
                      <span className="text-[9px] font-mono text-gray-600">
                        {d.platform === "all" ? "全平台" : PLATFORM_META[d.platform as MarketingPlatform].label}
                      </span>
                      <span className="text-[9px] font-mono text-gray-700">·</span>
                      <span className="text-[9px] font-mono text-gray-600">{CATEGORY_LABEL[d.category]}</span>
                    </div>
                    <div className="text-[11px] text-gray-200 leading-snug">{d.symptom}</div>
                  </div>
                  {isExpanded ? (
                    <ChevronDown size={11} className="text-gray-600 flex-shrink-0 mt-1" />
                  ) : (
                    <ChevronRight size={11} className="text-gray-600 flex-shrink-0 mt-1" />
                  )}
                </button>

                {isExpanded && (
                  <div className="px-2.5 pb-2.5 pt-0 space-y-1.5 border-t border-white/[0.03] mt-0.5">
                    <div className="pt-2">
                      <div className="flex items-start gap-1.5">
                        <span className="text-[9px] font-mono text-gray-600 uppercase tracking-wider flex-shrink-0 mt-0.5">原因：</span>
                        <span className="text-[10px] text-gray-400 leading-snug">{d.cause}</span>
                      </div>
                      <div className="flex items-start gap-1.5 mt-1">
                        <Lightbulb size={10} className="text-amber-400 flex-shrink-0 mt-0.5" />
                        <span className="text-[10px] text-gray-300 leading-snug">{d.suggestion}</span>
                      </div>
                      <div className="flex items-start gap-1.5 mt-1">
                        <TrendingUp size={10} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                        <span className="text-[10px] text-emerald-300/80 leading-snug">{d.expectedGain}</span>
                      </div>
                    </div>
                    {(d.severity === "critical" || d.severity === "warning") && (
                      <button
                        onClick={() => setWorkspace("media_studio")}
                        className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono uppercase bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/25 transition-colors mt-1"
                      >
                        <ExternalLink size={9} />
                        前往媒体工作室优化
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </motion.div>
  );
}
